// src/ui/UIUpgradeManager.js - Safe UI Migration Manager
// Wraps existing components with new glass morphism styling without touching logic

export class UIUpgradeManager {
    constructor() {
        // FORCE new UI by default - override localStorage completely
        console.log('🔧 UIUpgradeManager: FORCING new UI mode');
        this.isNewUIEnabled = true;
        localStorage.setItem('enableNewUI', 'true');
        this.wrappedComponents = new Map();
        this.init();
    }

    init() {
        console.log('🎨 UIUpgradeManager: Initializing safe UI migration system');
        console.log(`🎨 Current UI mode: ${this.isNewUIEnabled ? 'NEW' : 'LEGACY'}`);
        
        // Apply current UI mode
        if (this.isNewUIEnabled) {
            this.enableNewUI();
        } else {
            this.enableLegacyUI();
        }
        
        // Make toggle function globally available
        window.toggleUI = () => this.toggleUI();
        window.enableNewUI = () => this.enableNewUI();
        window.enableLegacyUI = () => this.enableLegacyUI();
        
        console.log('🎨 UIUpgradeManager: Use window.toggleUI() to switch between old and new styles');
    }

    toggleUI() {
        this.isNewUIEnabled = !this.isNewUIEnabled;
        localStorage.setItem('enableNewUI', this.isNewUIEnabled.toString());
        
        console.log(`🎨 UI toggled to: ${this.isNewUIEnabled ? 'NEW' : 'LEGACY'}`);
        
        if (this.isNewUIEnabled) {
            this.enableNewUI();
        } else {
            this.enableLegacyUI();
        }
        
        // Show notification
        this.showNotification(`UI switched to ${this.isNewUIEnabled ? 'NEW' : 'LEGACY'} design`);
    }

    enableNewUI() {
        console.log('🎨 Enabling new glass morphism UI');
        document.body.classList.add('new-ui');
        document.body.classList.remove('legacy-ui');
        
        // COMPLETELY DISABLED: All wrapper components - now using proper template implementation
        console.log('🎨 UIUpgradeManager: All wrapping disabled - using template scenes instead');
        console.log('🎨 Template scenes now handle their own glass morphism styling');
        
        // DISABLED: Even PPT Hero wrapping for now to eliminate interference
        // this.wrapPPTHeroRatingSystem();
        
        this.isNewUIEnabled = true;
        localStorage.setItem('enableNewUI', 'true');
    }

    enableLegacyUI() {
        console.log('📝 Enabling legacy UI');
        document.body.classList.add('legacy-ui');
        document.body.classList.remove('new-ui');
        
        // Remove all wrappers to restore original components
        this.unwrapAllComponents();
        
        this.isNewUIEnabled = false;
        localStorage.setItem('enableNewUI', 'false');
    }

    wrapAuthenticationComponents() {
        console.log('🔐 Wrapping authentication components with glass morphism');
        
        // Wrap auth overlay with new styling
        const authOverlay = document.getElementById('auth-overlay');
        if (authOverlay && !this.wrappedComponents.has('auth-overlay')) {
            const wrapper = this.createGlassWrapper('auth-overlay-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="glass-morphism-card auth-card">
                        ${authOverlay.innerHTML}
                    </div>
                </div>
            `;
            
            authOverlay.parentNode.insertBefore(wrapper, authOverlay);
            authOverlay.style.display = 'none';
            this.wrappedComponents.set('auth-overlay', { original: authOverlay, wrapper: wrapper });
        }
    }

    wrapAnalyticsComponents() {
        console.log('📊 Wrapping analytics components with glass morphism from desktop-analytics-dashboard.html template');
        
        // Wrap analytics overlay with new styling from template
        const analyticsOverlay = document.getElementById('analytics-overlay');
        if (analyticsOverlay && !this.wrappedComponents.has('analytics-overlay')) {
            const wrapper = this.createGlassWrapper('analytics-overlay-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="analytics-dashboard-container">
                        <!-- Header from template -->
                        <div class="analytics-header">
                            <div class="header-left">
                                <h1 class="analytics-title">Performance Analytics</h1>
                                <p class="analytics-subtitle">Data-driven insights for executive excellence</p>
                            </div>
                            <div class="header-actions">
                                <button class="export-btn">Export Data</button>
                                <button class="glass-close-btn" onclick="window.uiUpgradeManager.closeAnalytics()">×</button>
                            </div>
                        </div>
                        
                        <!-- Analytics Content with Glass Morphism Cards -->
                        <div class="analytics-content">
                            <div class="analytics-grid">
                                <div class="analytics-card">
                                    <div class="card-header">
                                        <h3 class="card-title">Performance Overview</h3>
                                        <span class="card-icon">📊</span>
                                    </div>
                                    <div class="card-content">
                                        ${analyticsOverlay.innerHTML}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            analyticsOverlay.parentNode.insertBefore(wrapper, analyticsOverlay);
            analyticsOverlay.style.display = 'none';
            this.wrappedComponents.set('analytics-overlay', { original: analyticsOverlay, wrapper: wrapper });
        }
    }

    wrapPPTHeroRatingSystem() {
        console.log('🎯 Wrapping PPT Hero rating system with glass morphism (PRESERVE ALL LOGIC)');
        
        // Find PPT Hero performance rating overlay and wrap it
        const performanceOverlay = document.getElementById('performance-rating-overlay');
        if (performanceOverlay && !this.wrappedComponents.has('performance-rating-overlay')) {
            const wrapper = this.createGlassWrapper('performance-rating-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="glass-morphism-dashboard">
                        <div class="dashboard-header">
                            <h2 class="dashboard-title">Performance Rating</h2>
                            <button class="glass-close-btn" onclick="window.uiUpgradeManager.closePPTHeroRating()">×</button>
                        </div>
                        <div class="dashboard-content">
                            ${performanceOverlay.innerHTML}
                        </div>
                    </div>
                </div>
            `;
            
            performanceOverlay.parentNode.insertBefore(wrapper, performanceOverlay);
            performanceOverlay.style.display = 'none';
            this.wrappedComponents.set('performance-rating-overlay', { original: performanceOverlay, wrapper: wrapper });
        }
        
        // Find and wrap any rating containers in PPT Hero scenes
        const ratingContainers = document.querySelectorAll('.rating-container, .star-rating, .performance-rating, .score-display');
        ratingContainers.forEach((container, index) => {
            if (!this.wrappedComponents.has(`rating-${index}`)) {
                const wrapper = this.createGlassWrapper(`rating-wrapper-${index}`);
                wrapper.innerHTML = `
                    <div class="glass-morphism-rating-card">
                        ${container.outerHTML}
                    </div>
                `;
                
                container.parentNode.insertBefore(wrapper, container);
                container.style.display = 'none';
                this.wrappedComponents.set(`rating-${index}`, { original: container, wrapper: wrapper });
            }
        });
        
        console.log('🎯 PPT Hero rating system wrapped - ALL LOGIC PRESERVED');
    }

    wrapNavigationComponents() {
        console.log('🧭 Wrapping navigation components with glass morphism');
        
        // The navigation is already updated with responsive design
        // Just add glass morphism class
        const topRibbon = document.getElementById('top-ribbon');
        if (topRibbon) {
            topRibbon.classList.add('glass-morphism-nav');
        }
    }

    createGlassWrapper(id) {
        const wrapper = document.createElement('div');
        wrapper.id = id;
        wrapper.className = 'ui-upgrade-wrapper';
        return wrapper;
    }

    unwrapAllComponents() {
        console.log('🔄 Unwrapping all components to restore original UI');
        
        this.wrappedComponents.forEach(({ original, wrapper }, key) => {
            if (wrapper.parentNode) {
                wrapper.parentNode.insertBefore(original, wrapper);
                wrapper.parentNode.removeChild(wrapper);
                original.style.display = '';
            }
        });
        
        this.wrappedComponents.clear();
        
        // Remove glass morphism classes
        const topRibbon = document.getElementById('top-ribbon');
        if (topRibbon) {
            topRibbon.classList.remove('glass-morphism-nav');
        }
    }

    closeAnalytics() {
        // Delegate to existing analytics close functionality
        if (window.UIController && window.UIController.hideOverlay) {
            window.UIController.hideOverlay('analytics-overlay');
        }
    }

    wrapHallOfFameComponents() {
        console.log('🏆 Wrapping Hall of Fame components with glass morphism from desktop-hall-of-fame.html template');
        
        // Wrap leaderboard overlay with new styling
        const leaderboardOverlay = document.getElementById('leaderboard-overlay');
        if (leaderboardOverlay && !this.wrappedComponents.has('leaderboard-overlay')) {
            const wrapper = this.createGlassWrapper('leaderboard-overlay-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="hall-of-fame-container">
                        <!-- Header from template -->
                        <div class="hall-of-fame-header">
                            <div class="header-left">
                                <h1 class="hall-of-fame-title">Hall of Fame</h1>
                                <p class="hall-of-fame-subtitle">Top performers across all training modules</p>
                            </div>
                            <div class="header-actions">
                                <button class="filter-btn">Filter</button>
                                <button class="glass-close-btn" onclick="window.uiUpgradeManager.closeHallOfFame()">×</button>
                            </div>
                        </div>
                        
                        <!-- Leaderboard Content with Glass Morphism -->
                        <div class="hall-of-fame-content">
                            <div class="leaderboard-grid">
                                <div class="leaderboard-card">
                                    <div class="card-header">
                                        <h3 class="card-title">Global Rankings</h3>
                                        <span class="card-icon">🏆</span>
                                    </div>
                                    <div class="card-content">
                                        ${leaderboardOverlay.innerHTML}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            leaderboardOverlay.parentNode.insertBefore(wrapper, leaderboardOverlay);
            leaderboardOverlay.style.display = 'none';
            this.wrappedComponents.set('leaderboard-overlay', { original: leaderboardOverlay, wrapper: wrapper });
        }
    }

    closePPTHeroRating() {
        // Delegate to existing PPT Hero rating close functionality
        const performanceOverlay = document.getElementById('performance-rating-overlay');
        if (performanceOverlay && performanceOverlay.style.display !== 'none') {
            performanceOverlay.style.display = 'none';
        }
        
        // Also try to close via any existing close handlers
        if (window.closePerformanceRating) {
            window.closePerformanceRating();
        }
    }

    wrapProfileComponents() {
        console.log('👤 Wrapping Profile components with glass morphism from desktop/mobile-profile-management.html templates');
        
        // Wrap profile overlay/modal with new styling
        const profileOverlay = document.getElementById('profile-overlay') || document.querySelector('.profile-modal');
        if (profileOverlay && !this.wrappedComponents.has('profile-overlay')) {
            const wrapper = this.createGlassWrapper('profile-overlay-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="profile-management-container">
                        <!-- Header from template -->
                        <div class="profile-header">
                            <div class="header-left">
                                <h1 class="profile-title">Profile Management</h1>
                                <p class="profile-subtitle">Manage your account settings and preferences</p>
                            </div>
                            <div class="header-actions">
                                <button class="save-profile-btn">Save Changes</button>
                                <button class="glass-close-btn" onclick="window.uiUpgradeManager.closeProfile()">×</button>
                            </div>
                        </div>
                        
                        <!-- Profile Content with Glass Morphism -->
                        <div class="profile-content">
                            <div class="profile-grid">
                                <div class="profile-card">
                                    <div class="card-header">
                                        <h3 class="card-title">Account Information</h3>
                                        <span class="card-icon">👤</span>
                                    </div>
                                    <div class="card-content">
                                        ${profileOverlay.innerHTML}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            profileOverlay.parentNode.insertBefore(wrapper, profileOverlay);
            profileOverlay.style.display = 'none';
            this.wrappedComponents.set('profile-overlay', { original: profileOverlay, wrapper: wrapper });
        }
    }

    closeHallOfFame() {
        // Delegate to existing Hall of Fame close functionality
        if (window.UIController && window.UIController.hideOverlay) {
            window.UIController.hideOverlay('leaderboard-overlay');
        }
        
        // Also try direct hide
        const leaderboardOverlay = document.getElementById('leaderboard-overlay');
        if (leaderboardOverlay) {
            leaderboardOverlay.style.display = 'none';
        }
    }

    wrapGameCenterComponents() {
        console.log('🎮 Wrapping Game Center components with glass morphism from desktop-game-center.html and mobile-game-launcher.html templates');
        
        // Wrap game selection/launcher overlay with new styling
        const gameOverlay = document.getElementById('game-overlay') || document.querySelector('.game-selector');
        if (gameOverlay && !this.wrappedComponents.has('game-overlay')) {
            const wrapper = this.createGlassWrapper('game-overlay-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="game-center-container">
                        <!-- Header from template -->
                        <div class="game-center-header">
                            <div class="header-left">
                                <h1 class="game-center-title">Game Center</h1>
                                <p class="game-center-subtitle">Select your training module</p>
                            </div>
                            <div class="header-actions">
                                <button class="filter-games-btn">Filter</button>
                                <button class="glass-close-btn" onclick="window.uiUpgradeManager.closeGameCenter()">×</button>
                            </div>
                        </div>
                        
                        <!-- Game Center Content with Glass Morphism -->
                        <div class="game-center-content">
                            <div class="games-grid">
                                <div class="games-card">
                                    <div class="card-header">
                                        <h3 class="card-title">Available Games</h3>
                                        <span class="card-icon">🎮</span>
                                    </div>
                                    <div class="card-content">
                                        ${gameOverlay.innerHTML}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            gameOverlay.parentNode.insertBefore(wrapper, gameOverlay);
            gameOverlay.style.display = 'none';
            this.wrappedComponents.set('game-overlay', { original: gameOverlay, wrapper: wrapper });
        }
    }

    closeProfile() {
        // Delegate to existing Profile close functionality
        if (window.UIController && window.UIController.hideOverlay) {
            window.UIController.hideOverlay('profile-overlay');
        }
        
        // Also try direct hide
        const profileOverlay = document.getElementById('profile-overlay') || document.querySelector('.profile-modal');
        if (profileOverlay) {
            profileOverlay.style.display = 'none';
        }
    }

    wrapAdminPanelComponents() {
        console.log('🔧 Wrapping Admin Panel components with glass morphism from desktop-admin-panel.html template');
        
        // Wrap admin panel overlay with new styling
        const adminOverlay = document.getElementById('admin-overlay') || document.querySelector('.admin-panel');
        if (adminOverlay && !this.wrappedComponents.has('admin-overlay')) {
            const wrapper = this.createGlassWrapper('admin-overlay-wrapper');
            wrapper.innerHTML = `
                <div class="glass-morphism-overlay">
                    <div class="admin-panel-container">
                        <!-- Header from template -->
                        <div class="admin-panel-header">
                            <div class="header-left">
                                <h1 class="admin-panel-title">Admin Panel</h1>
                                <p class="admin-panel-subtitle">System administration and management</p>
                                <span class="admin-badge">ADMIN</span>
                            </div>
                            <div class="header-actions">
                                <button class="system-status-btn">System Status</button>
                                <button class="glass-close-btn" onclick="window.uiUpgradeManager.closeAdminPanel()">×</button>
                            </div>
                        </div>
                        
                        <!-- Admin Panel Content with Glass Morphism -->
                        <div class="admin-panel-content">
                            <div class="admin-grid">
                                <div class="admin-card">
                                    <div class="card-header">
                                        <h3 class="card-title">Administrative Controls</h3>
                                        <span class="card-icon">🔧</span>
                                    </div>
                                    <div class="card-content">
                                        ${adminOverlay.innerHTML}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            adminOverlay.parentNode.insertBefore(wrapper, adminOverlay);
            adminOverlay.style.display = 'none';
            this.wrappedComponents.set('admin-overlay', { original: adminOverlay, wrapper: wrapper });
        }
    }

    closeGameCenter() {
        // Delegate to existing Game Center close functionality
        if (window.UIController && window.UIController.hideOverlay) {
            window.UIController.hideOverlay('game-overlay');
        }
        
        // Also try direct hide
        const gameOverlay = document.getElementById('game-overlay') || document.querySelector('.game-selector');
        if (gameOverlay) {
            gameOverlay.style.display = 'none';
        }
    }

    closeAdminPanel() {
        // Delegate to existing Admin Panel close functionality
        if (window.UIController && window.UIController.hideOverlay) {
            window.UIController.hideOverlay('admin-overlay');
        }
        
        // Also try direct hide
        const adminOverlay = document.getElementById('admin-overlay') || document.querySelector('.admin-panel');
        if (adminOverlay) {
            adminOverlay.style.display = 'none';
        }
    }

    showNotification(message) {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: rgba(0, 212, 255, 0.9);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            z-index: 10001;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            animation: slideIn 0.3s ease;
        `;
        notification.textContent = message;
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
            if (notification.parentNode) {
                notification.style.animation = 'slideOut 0.3s ease';
                setTimeout(() => {
                    notification.parentNode.removeChild(notification);
                }, 300);
            }
        }, 2000);
    }
}

// Initialize on DOM ready
let uiUpgradeManager;

document.addEventListener('DOMContentLoaded', () => {
    if (!uiUpgradeManager) {
        uiUpgradeManager = new UIUpgradeManager();
        window.uiUpgradeManager = uiUpgradeManager;
        console.log('🎨 UIUpgradeManager ready - Use window.toggleUI() to test new designs');
    }
});

// Immediate initialization if DOM is already ready
if (document.readyState === 'loading') {
    // DOM is still loading
} else {
    // DOM is already loaded
    if (!uiUpgradeManager) {
        uiUpgradeManager = new UIUpgradeManager();
        window.uiUpgradeManager = uiUpgradeManager;
        console.log('🎨 UIUpgradeManager ready - Use window.toggleUI() to test new designs');
    }
}

export default UIUpgradeManager;
